import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Wallet } from "lucide-react";
import { BarChart, Bar, Cell, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine, LabelList } from "recharts";
import { endringsLinjer } from "@/components/endringMerke";
import { formatNumber, dec, num } from "@/lib/formatters";
import { tooltipStyle } from "./oversiktConstants";

const f1 = (v) => (v == null ? "—" : dec(v, 1));
const f2 = (v) => (v == null ? "—" : dec(v, 2));

const ArbeidskapitalCompareChart = ({ arbeidskapitalData, colors }) => {
  const rows = Object.values(arbeidskapitalData?.kommuner || {}).filter((k) => k.latest?.pct_av_driftsinntekter != null);
  if (rows.length === 0) return null;
  const year = rows[0].latest.year;
  const land = (arbeidskapitalData.land || []).find((r) => r.year === year);
  const bars = rows.map((r) => ({ name: r.name, pct: r.latest.pct_av_driftsinntekter, lg: r.latest.likviditetsgrad }));
  const years = [...new Set(rows.flatMap((r) => r.history.map((h) => h.year)))].sort();
  const trend = years.map((y) => ({ year: y, ...Object.fromEntries(rows.map((r) => [r.name, r.history.find((h) => h.year === y)?.pct_av_driftsinntekter ?? null])), Landssnitt: (arbeidskapitalData.land || []).find((r) => r.year === y)?.pct_av_driftsinntekter ?? null }));
  return (
    <Card className="bg-white border border-[#E6E9F0] rounded-lg lg:col-span-2" data-testid="compare-arbeidskapital-chart">
      <CardHeader className="border-b border-[#E6E9F0] pb-4">
        <CardTitle className="text-lg font-medium text-[#1F2A36] flex items-center gap-2">
          <Wallet className="w-5 h-5 text-[#103766]" />
          Arbeidskapital og likviditet {year}
        </CardTitle>
        <p className="text-sm text-[#4B647F]">Arbeidskapital (omløpsmidlar − kortsiktig gjeld) i prosent av driftsinntektene{land ? `; landssnitt ${f1(land.pct_av_driftsinntekter)} %` : ""}. Likviditetsgrad = omløpsmidler / kortsiktig gjeld{land ? ` (landssnitt ${f2(land.likviditetsgrad)})` : ""}.</p>
      </CardHeader>
      <CardContent className="pt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="h-[300px]" data-testid="compare-arbeidskapital-bars">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={bars} barCategoryGap="35%" margin={{ top: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E6E9F0" />
              <XAxis dataKey="name" stroke="#8AA4BF" fontSize={12} />
              <YAxis stroke="#8AA4BF" fontSize={12} tickFormatter={(v) => `${num(v)} %`} />
              <Tooltip formatter={(v, _n, p) => [`${f1(v)} % av driftsinntektene (likviditetsgrad ${f2(p.payload.lg)})`, "Arbeidskapital"]} contentStyle={tooltipStyle} />
              {land && <ReferenceLine y={land.pct_av_driftsinntekter} stroke="#4B647F" strokeDasharray="4 4" label={{ value: "Landet", position: "insideTopRight", fontSize: 11, fill: "#4B647F" }} />}
              <ReferenceLine y={0} stroke="#4B647F" />
              <Bar dataKey="pct" radius={[4, 4, 0, 0]}>
                {bars.map((b, i) => <Cell key={b.name} fill={b.pct < 0 ? "#C0392B" : colors[i % colors.length]} />)}
                <LabelList dataKey="pct" position="top" formatter={(v) => `${f1(v)} %`} fontSize={12} fill="#1F2A36" />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="h-[300px]" data-testid="compare-arbeidskapital-trend">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={trend}>
              {endringsLinjer(trend)}
              <CartesianGrid strokeDasharray="3 3" stroke="#E6E9F0" />
              <XAxis dataKey="year" stroke="#8AA4BF" fontSize={12} />
              <YAxis stroke="#8AA4BF" fontSize={12} tickFormatter={(v) => `${num(v)} %`} />
              <Tooltip formatter={(v) => `${f1(v)} %`} contentStyle={tooltipStyle} />
              <Legend itemSorter={null} />
              {rows.map((r, i) => <Line key={r.name} type="monotone" dataKey={r.name} stroke={colors[i % colors.length]} strokeWidth={2} dot={{ r: 3 }} connectNulls />)}
              <Line type="monotone" dataKey="Landssnitt" stroke="#9CA3AF" strokeWidth={2} strokeDasharray="4 4" dot={false} connectNulls />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="lg:col-span-2 overflow-x-auto">
          <table className="w-full text-sm" data-testid="compare-arbeidskapital-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-[0.1em] text-[#8AA4BF] border-b border-[#E6E9F0]">
                <th className="py-2 pr-3 font-semibold">Kommune</th>
                <th className="py-2 px-3 font-semibold text-right">Omløpsmidlar</th>
                <th className="py-2 px-3 font-semibold text-right">Kortsiktig gjeld</th>
                <th className="py-2 px-3 font-semibold text-right">Arbeidskapital</th>
                <th className="py-2 px-3 font-semibold text-right">% av driftsinnt.</th>
                <th className="py-2 pl-3 font-semibold text-right">Likviditetsgrad</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.name} className="border-b border-[#F1F4F8] text-[#1F2A36]" data-testid={`compare-arbeidskapital-row-${r.name}`}>
                  <td className="py-2 pr-3 font-medium">{r.name}</td>
                  <td className="py-2 px-3 text-right tabular-nums">{formatNumber(Math.round(r.latest.omlopsmidler / 1000))} mill.</td>
                  <td className="py-2 px-3 text-right tabular-nums">{formatNumber(Math.round(r.latest.kortsiktig_gjeld / 1000))} mill.</td>
                  <td className={`py-2 px-3 text-right tabular-nums font-medium ${r.latest.arbeidskapital >= 0 ? "text-[#2F7D5B]" : "text-[#C0392B]"}`}>{formatNumber(Math.round(r.latest.arbeidskapital / 1000))} mill.</td>
                  <td className="py-2 px-3 text-right tabular-nums">{f1(r.latest.pct_av_driftsinntekter)} %</td>
                  <td className={`py-2 pl-3 text-right tabular-nums ${r.latest.likviditetsgrad < 1 ? "text-[#C0392B]" : ""}`}>{f2(r.latest.likviditetsgrad)}</td>
                </tr>
              ))}
              {land && (
                <tr className="text-[#4B647F] bg-[#F8FAFC]" data-testid="compare-arbeidskapital-row-land">
                  <td className="py-2 pr-3 font-medium">Landssnitt ({land.kommune_count} kommunar)</td>
                  <td colSpan={3} />
                  <td className="py-2 px-3 text-right tabular-nums">{f1(land.pct_av_driftsinntekter)} %</td>
                  <td className="py-2 pl-3 text-right tabular-nums">{f2(land.likviditetsgrad)}</td>
                </tr>
              )}
            </tbody>
          </table>
          <p className="text-xs text-[#8AA4BF] text-center mt-3">Kjelde: SSB 13545 – balanserekneskap, kommunekasse (1000 kr). Landssnitt er uvekta gjennomsnitt av kommunane sine verdiar.</p>
        </div>
      </CardContent>
    </Card>
  );
};

export default ArbeidskapitalCompareChart;
